import { Link, useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import {
  LayoutDashboard, Truck, DollarSign, UserCircle,
  LogOut, Menu, X, Shield, ChevronRight,
  ChevronDown, Users, BarChart3, Wrench, Settings, Calculator,
  Sun, Moon, MessageSquare, Home, Megaphone
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { findMyDriverProfile } from '@/lib/myDriverProfile';
import AIDispatchChat from './AIDispatchChat';
import { useTheme } from '@/lib/ThemeContext';
import CompleteStaffProfileGate from './CompleteStaffProfileGate';
import CompleteW4Gate from './CompleteW4Gate';
import FeedbackModal from './FeedbackModal';
import { avatarColorClasses } from '@/lib/avatarColor';
import { countUnreadForUser, subscribeStaffMessages, subscribeReads } from '@/lib/teamChat';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
// Icon-only Pro Haul wheel for the sidebar brand block. Sits next to
// the dynamic carrier name; the horizontal-logo wordmark would crowd
// the narrow w-64 sidebar. When a carrier later uploads a custom
// logo (carrierLogoUrl on AuthContext), the sidebar swaps to that;
// otherwise this default Pro Haul wheel renders.
import defaultCarrierIconUrl from '@/assets/icon-only.png';

const LS_KEY = (email) => `last_seen_messages_${email}`;

const TMS_SECTIONS = [
  { id: 'dashboard',   label: 'Dashboard',   icon: Home },
  { id: 'dispatch',    label: 'Dispatch',    icon: LayoutDashboard },
  { id: 'hr',         label: 'HR',           icon: Users },
  // Payroll lives inside Accounting now (see AdminPanel.jsx's
  // ALL_PARENT_TABS) -- same department, one place for anything money-related.
  { id: 'accounting', label: 'Accounting',   icon: Calculator },
  { id: 'reports',    label: 'Reports',      icon: BarChart3 },
  { id: 'maintenance',label: 'Maintenance',  icon: Wrench },
  { id: 'settings',   label: 'Settings',     icon: Settings },
];

const TMS_ROLE_ACCESS = {
  super_admin: ['dashboard', 'dispatch', 'hr', 'accounting', 'reports', 'maintenance', 'settings'],
  hr: ['dashboard', 'hr'],
  accounting: ['dashboard', 'accounting'],
  dispatch: ['dashboard', 'dispatch'],
};

// Plan-based section gates layered on top of role access. Owner-Operator
// is a single-person operation: hiding HR (no one to hire), the
// multi-driver Dispatch board, and Maintenance (an O/O runs their own
// truck's upkeep outside the TMS -- persona research this session found
// no O/O wanted a fleet maintenance module) keeps the UI clean and
// matches the value prop of the lower-priced plan. Reports stays
// visible but is trimmed to just P&L + IRP Miles in AdminPanel.jsx's
// own plan filter (Reports itself is a shared section, not fully
// blocked). Payroll's own sub-tabs (now inside Accounting) are
// separately filtered in AdminPanel.jsx since Accounting itself still
// has non-payroll content (invoices, fuel advances) an owner-op does
// need. Fleet sees everything.
const PLAN_BLOCKED_SECTIONS = {
  owner_op: ['dispatch', 'hr', 'maintenance'],
  fleet:    [],
};

const ROLE_BADGE = {
  super_admin: { label: 'Super Admin', color: 'text-yellow-400' },
  hr: { label: 'HR', color: 'text-blue-400' },
  accounting: { label: 'Accounting', color: 'text-green-400' },
  dispatch: { label: 'Dispatch', color: 'text-purple-400' },
};

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, staffRole, isStaff, logout, carrierPlan, carrierName, carrierLogoUrl, carrierId, aiDispatchAddon } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tmsOpen, setTmsOpen] = useState(location.pathname === '/admin');
  const [driverProfile, setDriverProfile] = useState(undefined); // undefined = loading
  const [staffProfileRow, setStaffProfileRow] = useState(undefined); // undefined = loading
  const [w4Form, setW4Form] = useState(undefined); // undefined = loading

  const [unreadCount, setUnreadCount] = useState(0);
  const [dismissedWarnings, setDismissedWarnings] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    // Staff members don't need a driver profile lookup
    if (isStaff) { setDriverProfile(null); return; }
    if (!user?.email) { setDriverProfile(null); return; }
    findMyDriverProfile(user.email).then(setDriverProfile);
  }, [user?.email, isStaff]);

  const loadStaffProfileRow = () => {
    if (!isStaff || !user?.id) { setStaffProfileRow(null); return; }
    // Fetched flat via client.entities (not AuthContext's raw staffProfile,
    // whose full_name/phone/address live unwrapped inside `.data` there) so
    // the completeness check below reads real values, not undefined.
    client.entities.StaffProfile.filter({ user_id: user.id }, '-created_date', 1).then(rows => {
      setStaffProfileRow(rows[0] || null);
    }).catch(() => {
      // A failed fetch here previously left staffProfileRow stuck at
      // undefined forever -- the whole app hangs on the loading
      // spinner with zero feedback, indistinguishable from a crash.
      // Fall back to null so the page can still render (worst case,
      // the completeness gate re-prompts once the retry succeeds).
      setStaffProfileRow(null);
    });
  };
  useEffect(loadStaffProfileRow, [user?.id, isStaff]);

  const loadW4Form = () => {
    if (!isStaff || !user?.id) { setW4Form(null); return; }
    client.entities.StaffW4Form.filter({ user_id: user.id, tax_year: 2026 }, '-created_date', 1).then(rows => {
      setW4Form(rows[0] || null);
    }).catch(() => {
      setW4Form(null);
    });
  };
  useEffect(loadW4Form, [user?.id, isStaff]);

  // Team Chat nav badge -- count of conversations with unread activity
  // (not raw message count, same convention as Slack/Teams/Instagram).
  // Recomputed on route change (so leaving Team Chat updates it after
  // reading things there) and live via realtime on new messages/reads.
  const [teamChatUnread, setTeamChatUnread] = useState(0);
  const loadTeamChatUnread = () => {
    if (!isStaff || !user?.id || !carrierId) { setTeamChatUnread(0); return; }
    countUnreadForUser(carrierId, user.id).then(setTeamChatUnread).catch(() => {});
  };
  useEffect(loadTeamChatUnread, [isStaff, user?.id, carrierId, location.pathname]);
  useEffect(() => {
    if (!isStaff || !carrierId) return;
    const unsubMsgs = subscribeStaffMessages(carrierId, loadTeamChatUnread);
    const unsubReads = subscribeReads(carrierId, loadTeamChatUnread);
    return () => { unsubMsgs(); unsubReads(); };
  }, [isStaff, carrierId]);

  // Update last_seen when navigating to /active-load
  useEffect(() => {
    if (location.pathname === '/active-load' && user?.email) {
      localStorage.setItem(LS_KEY(user.email), new Date().toISOString());
      setUnreadCount(0);
    }
  }, [location.pathname, user?.email]);

  // Poll for unread messages
  useEffect(() => {
    if (!user?.email) return;

    const checkUnread = async () => {
      try {
        let activeLoad = null;
        if (isStaff) {
          const loads = await client.entities.Load.filter({ status: 'In Transit' }, '-updated_date', 1);
          if (!loads.length) {
            const bookedLoads = await client.entities.Load.filter({ status: 'Booked' }, '-updated_date', 1);
            activeLoad = bookedLoads[0] || null;
          } else {
            activeLoad = loads[0];
          }
        } else {
          const loads = await client.entities.Load.filter({ assigned_driver_email: user.email });
          activeLoad = loads.find(l => l.status === 'In Transit' || l.status === 'Booked') || null;
        }

        if (!activeLoad) { setUnreadCount(0); return; }

        const lastSeen = localStorage.getItem(LS_KEY(user.email));
        const messages = await client.entities.LoadMessage.filter({ load_id: activeLoad.id });

        const unread = messages.filter(m => {
          if (!lastSeen || new Date(m.created_date) > new Date(lastSeen)) {
            if (isStaff) return m.sender_role !== 'admin';
            return m.sender_email !== user.email;
          }
          return false;
        });

        setUnreadCount(unread.length);
      } catch {
        // Silently ignore
      }
    };

    checkUnread();
    const interval = setInterval(checkUnread, 30000);
    return () => clearInterval(interval);
  }, [user?.email, isStaff, location.pathname]);

  // Owner-Op's assets, for the expiry banner below. The full Maintenance
  // tab is gated out of that plan (AdminPanel.jsx's OWNER_OP_BLOCKED_PARENTS),
  // so without this their one or two units' registration/plate/inspection
  // dates would surface nowhere in the app at all -- competitor research
  // (TruckingOffice) found this is a top reason solo owner-operators cite
  // for staying on a tool, so it survives the Maintenance gate on its own.
  const [ownerOpAssets, setOwnerOpAssets] = useState([]);
  useEffect(() => {
    if (!isStaff || carrierPlan !== 'owner_op') { setOwnerOpAssets([]); return; }
    client.entities.Asset.list('-created_date', 200)
      .then(rows => setOwnerOpAssets(rows.filter(a => a.is_active)))
      .catch(() => setOwnerOpAssets([]));
  }, [isStaff, carrierPlan]);

  // Expiry warnings: CDL/med card for drivers, truck plate/registration/
  // inspection for Owner-Op staff (see ownerOpAssets above).
  const expiryWarnings = useMemo(() => {
    const today = new Date();
    const warnings = [];
    const check = (dateStr, label) => {
      if (!dateStr) return;
      const exp = new Date(dateStr);
      const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
      if (days <= 60) warnings.push({ label, days });
    };
    if (!isStaff && driverProfile) {
      check(driverProfile.cdl_expiration_date, 'CDL');
      check(driverProfile.med_card_expiration_date, 'Medical Card');
    }
    if (isStaff && carrierPlan === 'owner_op') {
      ownerOpAssets.forEach(a => {
        const unit = a.unit_number ? `Unit ${a.unit_number}` : (a.asset_type || 'Asset');
        check(a.plate_expiration, `${unit} Plate`);
        check(a.registration_expiration, `${unit} Registration`);
        check(a.annual_inspection_due, `${unit} Inspection`);
      });
    }
    return warnings;
  }, [isStaff, driverProfile, carrierPlan, ownerOpAssets]);

  // Loading state — wait for driver profile fetch (staff skip this)
  if (!isStaff && driverProfile === undefined) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Loading state — wait for staff profile + W-4 fetch (drivers skip this)
  if (isStaff && (staffProfileRow === undefined || w4Form === undefined)) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Gate: a staff member must fill in their name before they can use the
  // rest of the app -- accept_invitation's staff_profiles mirror (0012)
  // never asked for or set one, so without this someone could go on
  // using the whole app identified only by their raw email forever
  // (e.g. showing up that way in Team Chat instead of their name).
  if (isStaff && staffProfileRow && !staffProfileRow.full_name?.trim()) {
    return <CompleteStaffProfileGate profile={staffProfileRow} onComplete={loadStaffProfileRow} />;
  }

  // Gate: right after the name/address step, a staff member must file a
  // Form W-4 before they can use the rest of the app -- payroll can't
  // withhold federal income tax correctly (or legally) without one on
  // file, and there was previously no way to collect one at all.
  if (isStaff && staffProfileRow && !w4Form) {
    return <CompleteW4Gate profile={staffProfileRow} onComplete={loadW4Form} />;
  }

  // Gate: redirect drivers to onboarding if they have no profile at all yet.
  // Staff are immune. A Pending driver (profile exists, just not approved
  // yet) is NOT blocked here anymore -- this used to short-circuit the
  // entire app shell (no sidebar, nothing but a "Sign out" link) for every
  // route including My Profile, so a driver waiting on approval had no way
  // to view/edit their info or upload documents in the meantime. Each
  // driver-facing page now gates itself individually (LoadBoard,
  // ActiveLoad, Financials show "Application Under Review"; My Profile
  // deliberately stays open) instead of one blanket gate here.
  if (!isStaff && driverProfile !== undefined && !driverProfile) {
    return <Navigate to="/onboarding" replace />;
  }

  // Every staff member -- including super_admin now -- lands on the TMS
  // Dashboard, not the driver-facing Load Board, whenever they're at /.
  // super_admin's Load Board nav item lives at its own /load-board path
  // (see buildNavItems below) so it stays reachable, just not the landing
  // page anymore.
  if (isStaff && location.pathname === '/') {
    return <Navigate to="/admin" replace />;
  }

  // Build navigation based on who is logged in
  const buildNavItems = () => {
    if (isStaff) {
      const items = [
        { path: '/admin', label: 'TMS', icon: Shield },
      ];
      if (staffRole === 'super_admin') {
        items.push(
          { path: '/load-board', label: 'Load Board', icon: LayoutDashboard },
          { path: '/active-load', label: 'Active Load', icon: Truck },
          { path: '/financials', label: 'Financials', icon: DollarSign },
        );
      }
      // Every staff member (not just super_admin) needs a way to change
      // their own password / contact info, not just the owner.
      items.push({ path: '/my-profile', label: 'My Profile', icon: UserCircle });
      // Internal staff messaging (channels + DMs) -- staff only, every role.
      // Owner-Op is a single-person operation: no teammates to message,
      // so the nav item (and the route itself, guarded in TeamChat.jsx)
      // is Fleet-only.
      if (carrierPlan !== 'owner_op') {
        items.push({ path: '/team-chat', label: 'Team Chat', icon: MessageSquare });
      }
      return items;
    }
    // Regular driver
    return [
      { path: '/', label: 'Load Board', icon: LayoutDashboard },
      { path: '/active-load', label: 'Active Load', icon: Truck },
      { path: '/financials', label: 'Financials', icon: DollarSign },
      { path: '/my-profile', label: 'My Profile', icon: UserCircle },
    ];
  };

  const visibleNav = buildNavItems();
  const roleBadge = staffRole ? ROLE_BADGE[staffRole] : null;
  const displayName = (isStaff ? staffProfileRow?.full_name : driverProfile?.full_name) || user?.email || '';
  const firstName = displayName.trim().split(/\s+/)[0] || 'Account';
  const avatarUrl = isStaff ? staffProfileRow?.avatar_url : null;

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className={cn(
        // Light mode: crisp pure-white surface + sharp gray-200 right
        // border. Dark mode: the original glass-card-dark frosted
        // effect over the dark canvas. dark: variants keep the dark
        // theme unchanged while light gets the explicit crisp styles.
        "fixed inset-y-0 left-0 z-50 w-64 flex flex-col transition-transform duration-300",
        "bg-white border-r border-gray-200",
        "dark:bg-transparent dark:glass-card-dark dark:border-border",
        mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}>
        {/* Sidebar brand block: carrier-custom logo (if uploaded) OR
            default Pro Haul wheel, next to the dynamic carrier name.
            When a carrier later uploads a custom logo, AuthContext
            will expose carrierLogoUrl and the sidebar swaps to it
            automatically -- no per-component changes needed.
            pt-[calc(...)] reserves space for the OS status bar/notch on
            native builds (Android 15+ draws app content edge-to-edge by
            default) -- env(safe-area-inset-top) is 0 on web/PWA, where
            the browser's own chrome already sits above this. */}
        <div className="flex items-center gap-3 px-4 pb-5 pt-[calc(1.25rem+env(safe-area-inset-top))] border-b border-gray-200 dark:border-border">
          <img
            src={carrierLogoUrl || defaultCarrierIconUrl}
            alt={carrierName ? `${carrierName} logo` : 'Pro Haul'}
            className="w-9 h-9 flex-shrink-0 object-contain"
          />
          <div className="flex-1 min-w-0">
            {/* Sans-serif (not stencil) so the carrier name reads
                cleanly at small size in a tight sidebar slot. Stencil
                fonts are for hero headlines, not inline labels --
                they get muddy/hard-to-scan at 16px and below. */}
            <h1 className="font-semibold text-[#231F20] dark:text-white text-base leading-tight truncate">
              {carrierName || 'Pro Haul'}
            </h1>
            <p className="text-xs text-slate-600 dark:text-muted-foreground">
              {isStaff ? 'Staff Portal' : driverProfile?.driver_type === 'Company Driver' ? 'Company Driver Suite' : 'Owner-Operator Suite'}
            </p>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden text-slate-500 hover:text-[#231F20] dark:text-muted-foreground dark:hover:text-foreground transition-colors p-1 flex-shrink-0"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNav.map(item => {
            const Icon = item.icon;
            const isTMSItem = item.path === '/admin';
            const isTMSPage = location.pathname === '/admin';
            const activeTMSSection = new URLSearchParams(location.search).get('section');

            if (isTMSItem) {
              // Section visibility = role access  ∩  plan-allowed sections.
              // PLAN_BLOCKED_SECTIONS hides HR/Payroll/multi-dispatch for
              // owner-op accounts (see comment on the const for rationale).
              const planBlocked = PLAN_BLOCKED_SECTIONS[carrierPlan] || [];
              const allowedSections = TMS_SECTIONS.filter(s =>
                (TMS_ROLE_ACCESS[staffRole] || []).includes(s.id) &&
                !planBlocked.includes(s.id)
              );
              return (
                <div key={item.path}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isTMSPage) { navigate('/admin'); }
                      setTmsOpen(o => !o);
                      setMobileOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                      isTMSPage
                        // Active: neutral dark fill, not brand red -- red
                        // is reserved for the logo mark and primary CTAs,
                        // not general nav chrome (too much red look).
                        ? "bg-slate-900 text-white dark:bg-white/10 dark:text-white"
                        // Inactive: high-contrast dark text on white in
                        // light mode; muted in dark mode. Hover surface
                        // explicit per-mode (slate-100 on white, white/5
                        // on dark) so it never goes invisible.
                        : "text-[#231F20] hover:bg-slate-100 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-white/5"
                    )}
                  >
                    <Shield className="w-4 h-4 flex-shrink-0" />
                    <span>TMS</span>
                    <ChevronDown className={cn("w-3.5 h-3.5 ml-auto transition-transform duration-200", tmsOpen && isTMSPage && "rotate-180")} />
                  </button>
                  {isTMSPage && tmsOpen && (
                    <div className="mt-1 ml-3 pl-3 border-l border-gray-200 dark:border-border space-y-0.5">
                      {allowedSections.map(section => {
                        const SIcon = section.icon;
                        const isActive = activeTMSSection
                          ? activeTMSSection === section.id
                          : allowedSections[0]?.id === section.id;
                        return (
                          <Link
                            key={section.id}
                            to={`/admin?section=${section.id}`}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                              "flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200",
                              isActive
                                // Active sub-item: neutral fill, not red
                                // -- see the parent TMS button above.
                                ? "bg-slate-200 text-[#231F20] font-semibold dark:bg-white/10 dark:text-foreground"
                                : "text-[#231F20] hover:bg-slate-100 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-white/5"
                            )}
                          >
                            <SIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            {section.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const active = location.pathname === item.path;
            const badgeCount = item.path === '/active-load' ? unreadCount
              : item.path === '/team-chat' ? teamChatUnread
              : 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group",
                  active
                    // Neutral dark fill, not brand red -- see the TMS
                    // button above for why.
                    ? "bg-slate-900 text-white dark:bg-white/10 dark:text-white"
                    : "text-[#231F20] hover:bg-slate-100 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-white/5"
                )}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{item.label}</span>
                {badgeCount > 0 && (
                  <span className="ml-auto flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none flex-shrink-0">
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
                {active && badgeCount === 0 && <ChevronRight className="w-3 h-3 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User -- avatar + first name trigger, opens an account menu
            upward (mirrors the account menu pattern in Claude's own
            sidebar: photo + first name at the bottom, click for a
            dropdown with profile/settings/sign-out). */}
        <div className="px-3 py-3 border-t border-gray-200 dark:border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 w-full px-2 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className={cn('w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0', avatarColorClasses(user?.id))}>
                    {firstName.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <span className="flex-1 min-w-0 text-left">
                  <span className="block text-sm font-medium text-[#231F20] dark:text-foreground truncate">{firstName}</span>
                  {roleBadge && <span className="block text-xs text-slate-500 dark:text-muted-foreground truncate">{roleBadge.label}</span>}
                </span>
                <ChevronDown className="w-4 h-4 text-slate-400 dark:text-muted-foreground flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" sideOffset={8} className="w-60 dark:bg-[#1a1a1a] dark:border-border">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                {roleBadge && (
                  <span className={cn("inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded text-xs font-medium bg-slate-800", roleBadge.color)}>
                    <Shield className="w-3 h-3" /> {roleBadge.label}
                  </span>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to="/my-profile" onClick={() => setMobileOpen(false)}>
                  <UserCircle className="w-4 h-4" /> My Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {isDark ? 'Light mode' : 'Dark mode'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFeedbackOpen(true)} className="cursor-pointer">
                <Megaphone className="w-4 h-4" /> Send Feedback
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => logout()} className="cursor-pointer text-[#E22129] focus:text-[#E22129]">
                <LogOut className="w-4 h-4" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen min-w-0 overflow-x-hidden">
        {/* Mobile header */}
        <header className="flex items-center justify-between px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] md:hidden border-b border-border glass-card-dark sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Truck className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-space font-semibold text-white">Carrier Portal</span>
          </div>
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -mr-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </header>

        {expiryWarnings.filter(w => !dismissedWarnings.includes(w.label)).map(w => (
          <div key={w.label} className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/15 border-b border-amber-500/30 text-amber-300 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-amber-400 font-bold">⚠</span>
              <span>
                Your <strong>{w.label}</strong> {w.days <= 0 ? 'has expired.' : `expires in ${w.days} day${w.days === 1 ? '' : 's'}.`} Please renew it as soon as possible.
              </span>
            </div>
            <button
              onClick={() => setDismissedWarnings(p => [...p, w.label])}
              className="text-amber-400 hover:text-amber-200 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        <main className="flex-1 min-w-0 overflow-x-hidden animate-fade-in">
          {/* Soft seat-cap banner retired with the per-driver pricing model
              (you pay for declared seats, no cap to warn about). */}
          <div className="p-3 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* AI Dispatcher — floating draggable chat. Mounted at the Layout
          level (not inside an individual page) so it stays open across
          client-side route changes. Chat state lives in AIDispatchContext
          so messages survive navigation but reset on browser refresh.
          Driver-facing only -- staff have the full TMS, not this. Gated
          behind the $15/mo add-on for Owner-Op (migration 0018); Fleet's
          price already assumes it's included for every driver seat. */}
      {driverProfile && driverProfile.approval_status === 'Approved' && (carrierPlan !== 'owner_op' || aiDispatchAddon) && (
        <AIDispatchChat driverProfile={driverProfile} />
      )}
    </div>
  );
}
